import { Fragment, memo, useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import Table from 'shell/components/shared/table';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse, HttpResponseList } from 'shell/models/http-response';
import { ResponseAnprData } from 'shell/models/person';
import { AnprVM, FiltersFormPerson, PersonSearchStorageVM } from 'shell/view-models/search-anpr';
import { useFormik } from 'formik';
import { transformSearchPersonDataset } from 'shell/utils/transform-search-person';
import { initialValuesPerson } from 'shell/constants/initial-values-search';
import { Navigate, createSearchParams, useNavigate } from 'react-router-dom';
import { Typography, useEventCallback } from '@mui/material';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { validationSchemaFiltersPerson, headCells } from 'shell/constants/person-list';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { ItemVM } from 'shell/view-models/item';
import { transformerPersonList } from 'shell/utils/transform-person-list';
import FiltersPerson from './filters';
import { PersonVM } from 'shell/view-models/search-person';
import { Order } from 'shell/view-models/order';
import { ActionProps } from 'shell/view-models/actions-props';
import { useUserProfile } from 'shell/hooks/use-user-profile';
// import { openPdf } from 'shell/utils/open-pdf';
import { Anomaly } from 'front-office/view-models/common-certificate-form';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import ModalWarning from '../modal-warning';

interface PersonSearchProp {
  setAnomalieList?: React.Dispatch<React.SetStateAction<Anomaly[] | null>>;
  setErrorModal?: (value: boolean) => void;
  errorModalClosed?: boolean;
  pathname?: string; // destination url
  action: (props: ActionProps) => JSX.Element;
  url?: string;
  addedTitle?: string;
  isNationalSearch?: boolean;
  isAire?: boolean;
  isRistampaBusta?: boolean;
  setWarning?: (warning?: string, person?: PersonVM, isNationalResearch?: string) => void;
  goBackUrl?: string;
  isOnlyNationalSearch?: boolean;
}

const PersonSearchComponent = ({
  pathname = '/persona/dettaglio',
  url = `${__PERSON_NEW_API_URL__}/persona`,
  action: Action,
  addedTitle,
  isNationalSearch = false,
  isOnlyNationalSearch = false,
  isAire = false,
  isRistampaBusta = false,
  setWarning,
  goBackUrl,
}: PersonSearchProp): React.ReactElement => {
  const personsRef = useRef<HTMLDivElement | null>(null);
  const [error] = useState<string | undefined>();
  const { userProfile } = useUserProfile();
  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const [warning, setWarningModal] = useState<string | undefined>('');

  const navigate = useNavigate();

  const personFilterTable = useFilterTable<PersonSearchStorageVM>();

  const [anpr, getAnpr] = useHttpFetch<HttpResponse<ResponseAnprData>, AnprVM<PersonVM>>(`${url}`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
    transformer: transformerPersonList,
  });

  const getAnprFunc = useEventCallback((filters: FiltersFormPerson) => {
    personFilterTable?.setValues({
      filters,
      subject: null,
    });
    const data = transformSearchPersonDataset({ ...filters, page: 0 }, isAire);
    (
      getAnpr({
        data,
      }) as ResponsePromise<HttpResponseList<ResponseAnprData>, AnprVM<PersonVM>>
    ).then((response) => {
      const responseTranformed = response as AnprVM<PersonVM>;

      if (responseTranformed?.persons?.length > 1) {
        personFilterTable?.setValues({
          subject: null,
          isChildComponentVisited: false,
          filters: {
            ...filters,
            isPersonsListVisited: true,
            isChildComponentVisited: false,
          },
        });
      }

      if (responseTranformed.warningInformation && isRistampaBusta && setWarning) {
        // controllo da effettuare solo se ristampa busta
        setWarning(
          responseTranformed.warningInformation,
          responseTranformed.persons[0],
          filterPersonForm.values?.commonSearch?.isNationalResearch?.toString() ||
            filterPersonForm.values?.subjectData?.isNationalResearch?.toString() ||
            'false'
        );

        return;
      }

      if (responseTranformed.warningInformation) {
        // controllo da effettuare solo se ristampa busta
        setWarningModal(responseTranformed.warningInformation);

        return;
      }

      if (responseTranformed?.persons?.length === 1) {
        personFilterTable?.setValues({
          subject: responseTranformed?.persons[0],
          isChildComponentVisited: false,
          filters: {
            ...filters,
            isPersonsListVisited: true,
            isChildComponentVisited: false,
          },
        });
      }

      if (
        responseTranformed?.persons?.length === 1 &&
        (responseTranformed?.errors?.length > 0 || responseTranformed?.warnings?.length > 0)
      ) {
        window.scrollTo(0, 0);
      }
      onVisibility(responseTranformed?.errors?.length > 0, 'error');
      onVisibility(responseTranformed?.warnings?.length > 0, 'warning');
    });
  });

  const onVisibility = useCallback(
    (open, type) => {
      const visibilityMessage = type === 'error' && !open ? undefined : open;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const onSubmit = useEventCallback((formValues: FiltersFormPerson) => {
    filterPersonForm.setFieldValue('page', 0);
    personFilterTable?.setValues({ filters: formValues, subject: null });
    //calling the fucntion to get  the anpr result
    getAnprFunc(formValues);

    // if (operatorAuthorizations.ListaOmonimi) {
    //   setError('3144 Esistono  omonimi  inserire  data nascita o via o codice fiscale');
    //   onVisibility(true, 'error');
    //   return;
    // }
  });

  useEffect(() => {
    if (
      (personFilterTable?.values?.filters?.isChildComponentVisited ||
        personFilterTable?.values?.isChildComponentVisited) &&
      personFilterTable?.values?.filters?.isPersonsListVisited
    ) {
      //calling the fucntion to the get the anpr result
      getAnprFunc(personFilterTable?.values?.filters);
      filterPersonForm.setValues(personFilterTable?.values?.filters);
    } else {
      personFilterTable?.setValues({
        subject: null,
        isChildComponentVisited: false,
        filters: {
          ...personFilterTable?.values,
          page: personFilterTable?.values?.filters?.page || 0,
          orders: personFilterTable?.values?.filters?.orders || {},
          rowsPerPage: personFilterTable?.values?.filters?.rowsPerPage || 10,
          isChildComponentVisited: false,
          isPersonsListVisited: false,
        },
      });
    }
  }, []);

  const filterPersonForm = useFormik<FiltersFormPerson>({
    initialValues: {
      ...(initialValuesPerson.filters || {}),
      subjectData: {
        ...initialValuesPerson.filters?.subjectData,
        name: '',
        surname: '',
        withoutName: false,
        withoutSurname: false,
        istatCode: '',
        anprId: '',
        municipalityId: '',
      },
      searchByFiscalCode: {
        ...initialValuesPerson.filters?.searchByFiscalCode,
      },
      commonSearch: {
        ...initialValuesPerson?.filters?.commonSearch,
        isNationalResearch: isNationalSearch || undefined,
        municipalityOfResidence: !isNationalSearch
          ? {
              id: userProfile.datiLogin?.codiceIstat,
              description: userProfile.datiLogin?.nomeComune || '',
              province: userProfile.datiLogin?.siglaProvincia || '',
            }
          : null,
      },
    },
    validationSchema: validationSchemaFiltersPerson,
    onSubmit,
    onReset: () => {
      setVisibility({ error: false, warning: false });
    },
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const body: Array<PersonVM> = useMemo(() => {
    const formattedBody: Array<PersonVM> = (
      ((anpr.data as AnprVM<PersonVM>)?.persons as Array<PersonVM>) || []
    ).map((person) => {
      return {
        ...person,
        action: (
          <Action cell={person} filterPersonForm={filterPersonForm.values} pathname={pathname} />
        ),
        warningInformation: (anpr.data as AnprVM<PersonVM>).warningInformation || undefined,
      };
    });

    return formattedBody;
  }, [anpr, Action]);

  useEffect(() => {
    if ((anpr.data as AnprVM<PersonVM>)?.persons?.length > 0) {
      personsRef?.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [anpr]);

  /*
  useEffect(() => {
    // filterPersonForm.setValues(filterTable?.values || initialValuesPerson);
  }, []);

  useEffect(() => {
    // return () => filterTable?.setValues(filterPersonForm.values);
  }, [filterPersonForm.values]);
  */

  const onSort = useEventCallback((orders: Order) => {
    const newDataset = transformSearchPersonDataset({ ...filterPersonForm.values, orders }, isAire);
    return (
      getAnpr({
        data: newDataset,
      }) as ResponsePromise<HttpResponseList<ResponseAnprData>, AnprVM<PersonVM>>
    ).then(() => {
      filterPersonForm.setFieldValue('orders', orders);
    });
  });

  const onChangePage = useEventCallback((page: number) => {
    filterPersonForm.setFieldValue('page', page || 0);
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    const newDataset = {
      ...filterPersonForm.values,
      rowsPerPage: rowsPerPage || 0,
      page: 0,
    };

    filterPersonForm.setValues(newDataset);
  });

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (error) {
      errorToVisualize.title = 'È presente un errore';
      errorToVisualize.messages = [
        {
          id: 1,
          value: error,
        },
      ];
    }

    if ((anpr.data as AnprVM<PersonVM>)?.errors?.length) {
      errorToVisualize.title =
        (anpr.data as AnprVM<PersonVM>)?.errors?.length > 1
          ? `Ci sono ${(anpr.data as AnprVM<PersonVM>)?.errors?.length} errori`
          : `C'è 1 errore`;
      // errorToVisualize.title = `Ci sono ${(anpr.data as AnprVM<PersonVM>)?.errors?.length} errori`;
      errorToVisualize.messages = (anpr.data as AnprVM<PersonVM>)?.errors;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [error, visibility.error, anpr.data]);

  return (
    <Fragment>
      <div className="pmi-pt-4 pmi-pb-3 pmi-d-flex pmi-align-items-center pmi-justify-content-between">
        <Typography
          color="secondary.main"
          style={{
            fontSize: '26px',
          }}
        >
          Ricerca Persona {addedTitle || ''}
        </Typography>
      </div>
      <SearchMessages
        type={MessageType.Error}
        title={errorMessage.title || ''}
        messages={errorMessage.messages || []}
        visibility={errorMessage.visibility || false}
        onChangeVisibility={onVisibility}
      />
      <SearchMessages
        type={MessageType.Warning}
        title={
          (anpr.data as AnprVM<PersonVM>)?.warnings?.length > 1
            ? `Ci sono ${(anpr.data as AnprVM<PersonVM>)?.warnings?.length} warning`
            : `C'è 1 warning`
        }
        messages={(anpr.data as AnprVM<PersonVM>)?.warnings}
        visibility={
          visibility.warning !== undefined
            ? visibility.warning
            : (anpr.data as AnprVM<PersonVM>)?.warnings?.length > 0
        }
        onChangeVisibility={onVisibility}
      />
      <FiltersPerson
        filterPersonForm={filterPersonForm}
        goBackUrl={goBackUrl}
        isOnlyNationalSearch={isOnlyNationalSearch}
        onSubmit={onSubmit}
        isNationalSearch={isNationalSearch}
      />
      {personFilterTable?.values?.subject &&
      (body || []).length === 1 &&
      !((body || [])[0]?.warningInformation && isRistampaBusta) ? (
        <Navigate
          to={{
            pathname,
            search: `?${createSearchParams({
              ricercaNazionale:
                filterPersonForm.values?.commonSearch?.isNationalResearch?.toString() ||
                filterPersonForm.values?.subjectData?.isNationalResearch?.toString() ||
                'false',
              codiceFiscale: (body || [])[0].fiscalCode || '',
              idSchedaSoggettoANPR: (body || [])[0].anpr || '',
              idFamigliaANPR: (body || [])[0].idFamilyAnpr || '',
              idSchedaSoggettoComune:
                (body || [])[0].idSchedaSoggettoComune?.idSchedaSoggetto || '',
              codicePersonaSipo: (body || [])[0].codicePersonaSipo || '',
              nome: (body || [])[0].name || '',
              cognome: (body || [])[0].surname || '',
              stato: (body || [])[0].statePerson || '',
            })}`,
          }}
        />
      ) : undefined}

      <div
        ref={personsRef}
        style={{
          marginTop: 10,
        }}
      >
        {anpr.data ? (
          !((body || []).length > 0) || visibility.error === false ? (
            <Typography className="pmi-pt-3" color="secondary.main">
              Nessun elemento trovato con la ricerca effettuata.
            </Typography>
          ) : (
            <Table
              ariaLabel="Lista Persone"
              page={filterPersonForm.values.page || 0}
              rowsPerPage={filterPersonForm.values.rowsPerPage || 0}
              body={body}
              count={body?.length || 0}
              head={headCells}
              onSort={onSort}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
              orders={filterPersonForm.values.orders || {}}
              isNotLayoutFixed
              noSort
              hideButtons
            />
          )
        ) : undefined}
      </div>
      {!!warning ? (
        <ModalWarning
          open={!!warning}
          onClose={() => {
            setWarningModal('');
            navigate({
              pathname: pathname,
              search: `?${createSearchParams({
                ricercaNazionale:
                  filterPersonForm.values?.commonSearch?.isNationalResearch?.toString() ||
                  filterPersonForm.values?.subjectData?.isNationalResearch?.toString() ||
                  'false',
                codiceFiscale: (body || [])[0].fiscalCode || '',
                idSchedaSoggettoANPR: (body || [])[0].anpr || '',
                idFamigliaANPR: (body || [])[0].idFamilyAnpr || '',
                idSchedaSoggettoComune:
                  (body || [])[0].idSchedaSoggettoComune?.idSchedaSoggetto || '',
                codicePersonaSipo: (body || [])[0].codicePersonaSipo || '',
                nome: (body || [])[0].name || '',
                cognome: (body || [])[0].surname || '',
                stato: (body || [])[0].statePerson || '',
              })}`,
            });
          }}
          message={warning}
        />
      ) : undefined}
    </Fragment>
  );
};

PersonSearchComponent.displayName = 'Person Search component';

const PersonSearch = memo(PersonSearchComponent);

export default PersonSearch;
